import { ref, computed } from 'vue';
import { useTerraStore } from '~/terra/store/terra.store';
import { services } from '~/common/installers/services';
// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
const ARCGIS_SOURCE_ID = 'arcgis-overlay-source';
const ARCGIS_LAYER_ID = 'arcgis-overlay-layer';


// ---------------------------------------------------------------------------
// URL helpers
// ---------------------------------------------------------------------------

/**
 * Normalises an ArcGIS service URL into its canonical base URL and service type.
 * Supports three forms:
 *   - …/MapServer/0   (MapServer with a specific layer index)
 *   - …/MapServer     (bare MapServer endpoint)
 *   - …/ImageServer   (ImageServer endpoint)
 *
 * @param {string} inputUrl
 * @param {string} [fallbackMapServerLayers='show:0']
 * @returns {{ serviceUrl: string, serviceType: string, mapServerLayers: string|null }}
 */
function normalizeArcgisServiceUrl(inputUrl, fallbackMapServerLayers = 'show:0') {
  const serviceUrl = (inputUrl || '').replace(/\/+$/, '');
  const mapServerLayerMatch = serviceUrl.match(/^(.*\/MapServer)\/(\d+)$/);

  if (mapServerLayerMatch) {
    return {
      serviceUrl: mapServerLayerMatch[1],
      serviceType: 'MapServer',
      mapServerLayers: `show:${mapServerLayerMatch[2]}`,
    };
  }
  if (serviceUrl.endsWith('/MapServer')) {
    return { serviceUrl, serviceType: 'MapServer', mapServerLayers: fallbackMapServerLayers };
  }
  if (serviceUrl.endsWith('/ImageServer')) {
    return { serviceUrl, serviceType: 'ImageServer', mapServerLayers: null };
  }
  // Unknown service type — fall back to ImageServer behaviour.
  return { serviceUrl, serviceType: 'Unknown', mapServerLayers: fallbackMapServerLayers };
}

/**
 * Picks the best export image format from the service metadata.
 * Preference order: PNG32 › PNG24 › PNG › JPG.
 * Falls back to `png32` when metadata is absent.
 *
 * @param {object|null} metadata  ArcGIS service metadata JSON.
 * @returns {string}
 */
function getBestMapServerFormat(metadata) {
  const supported = String(metadata?.supportedImageFormatTypes || '')
    .split(',')
    .map(f => f.trim().toUpperCase())
    .filter(Boolean);

  if (!supported.length) return 'png32';
  for (const fmt of ['PNG32', 'PNG24', 'PNG', 'JPG']) {
    if (supported.includes(fmt)) return fmt.toLowerCase();
  }
  return supported[0].toLowerCase();
}

/**
 * Builds the raster tile URL for the given service.
 * - MapServer  → /export       with layers, format, and transparent params.
 * - ImageServer → /exportImage  with jpgpng format.
 *
 * @param {object}      serviceInfo  Result of {@link normalizeArcgisServiceUrl}.
 * @param {string}      token
 * @param {object|null} [metadata]   Service metadata (used for format selection on MapServer).
 * @returns {string}
 */
function buildTileUrl(serviceInfo, token, metadata = null) {
  const { serviceUrl, serviceType, mapServerLayers } = serviceInfo;
  let tileUrl;

  if (serviceType === 'MapServer') {
    const format = getBestMapServerFormat(metadata);
    tileUrl = `${serviceUrl}/export?f=image&bbox={bbox-epsg-3857}&bboxSR=3857&imageSR=3857`
      + `&size=256,256&format=${format}&transparent=true&layers=${encodeURIComponent(mapServerLayers)}`;
  } else {
    tileUrl = `${serviceUrl}/exportImage?f=image&bbox={bbox-epsg-3857}&bboxSR=3857&imageSR=3857&size=256,256&format=jpgpng`;
  }

  return token ? `${tileUrl}&token=${encodeURIComponent(token)}` : tileUrl;
}

/**
 * Builds the metadata fetch URL for the service.
 *
 * @param {object} serviceInfo  Result of {@link normalizeArcgisServiceUrl}.
 * @param {string} token
 * @returns {string}
 */
function buildMetadataUrl(serviceInfo, token) {
  const base = `${serviceInfo.serviceUrl}?f=json`;
  return token ? `${base}&token=${encodeURIComponent(token)}` : base;
}

// ---------------------------------------------------------------------------
// Coordinate-projection helpers (ported from mapbox-test.jsx)
// ---------------------------------------------------------------------------

function getSpatialReferenceWkid(spatialReference) {
  return spatialReference?.latestWkid || spatialReference?.wkid || spatialReference?.wkt || null;
}

function webMercatorToLngLat(x, y) {
  const earthRadius = 6378137;
  const lng = (x / earthRadius) * (180 / Math.PI);
  const lat = (2 * Math.atan(Math.exp(y / earthRadius)) - Math.PI / 2) * (180 / Math.PI);
  return [lng, lat];
}

function utmToLngLat(easting, northing, zone, isNorthernHemisphere) {
  const a = 6378137;
  const eccentricitySquared = 0.00669438;
  const secondEccentricitySquared = eccentricitySquared / (1 - eccentricitySquared);
  const scaleFactor = 0.9996;
  const x = easting - 500000;
  let y = northing;
  if (!isNorthernHemisphere) y -= 10000000;
  const longOrigin = (zone - 1) * 6 - 180 + 3;
  const m = y / scaleFactor;
  const mu = m / (a * (1 - eccentricitySquared / 4 - (3 * eccentricitySquared ** 2) / 64 - (5 * eccentricitySquared ** 3) / 256));
  const e1 = (1 - Math.sqrt(1 - eccentricitySquared)) / (1 + Math.sqrt(1 - eccentricitySquared));
  const j1 = (3 * e1) / 2 - (27 * e1 ** 3) / 32;
  const j2 = (21 * e1 ** 2) / 16 - (55 * e1 ** 4) / 32;
  const j3 = (151 * e1 ** 3) / 96;
  const j4 = (1097 * e1 ** 4) / 512;
  const footprintLat = mu + j1 * Math.sin(2 * mu) + j2 * Math.sin(4 * mu) + j3 * Math.sin(6 * mu) + j4 * Math.sin(8 * mu);
  const sinFP = Math.sin(footprintLat);
  const cosFP = Math.cos(footprintLat);
  const tanFP = Math.tan(footprintLat);
  const c1 = secondEccentricitySquared * cosFP ** 2;
  const t1 = tanFP ** 2;
  const n1 = a / Math.sqrt(1 - eccentricitySquared * sinFP ** 2);
  const r1 = (a * (1 - eccentricitySquared)) / (1 - eccentricitySquared * sinFP ** 2) ** 1.5;
  const d = x / (n1 * scaleFactor);
  const lat = footprintLat - ((n1 * tanFP) / r1) * (d ** 2 / 2 - ((5 + 3 * t1 + 10 * c1 - 4 * c1 ** 2 - 9 * secondEccentricitySquared) * d ** 4) / 24 + ((61 + 90 * t1 + 298 * c1 + 45 * t1 ** 2 - 252 * secondEccentricitySquared - 3 * c1 ** 2) * d ** 6) / 720);
  const lng = longOrigin + ((d - ((1 + 2 * t1 + c1) * d ** 3) / 6 + ((5 - 2 * c1 + 28 * t1 - 3 * c1 ** 2 + 8 * secondEccentricitySquared + 24 * t1 ** 2) * d ** 5) / 120) / cosFP) * (180 / Math.PI);
  return [lng, lat * (180 / Math.PI)];
}

function projectPointToLngLat(x, y, spatialReference) {
  const wkid = Number(getSpatialReferenceWkid(spatialReference));
  if (wkid === 4326) return [x, y];
  if (wkid === 3857 || wkid === 102100 || wkid === 102113) return webMercatorToLngLat(x, y);
  if (wkid >= 32601 && wkid <= 32660) return utmToLngLat(x, y, wkid - 32600, true);
  if (wkid >= 32701 && wkid <= 32760) return utmToLngLat(x, y, wkid - 32700, false);
  throw new Error(`Unsupported ArcGIS spatial reference: ${wkid || 'unknown'}`);
}

function normalizeLngLatBounds(points) {
  const lngs = points.map(([lng]) => lng);
  const lats = points.map(([, lat]) => lat);
  return [
    [Math.min(...lngs), Math.min(...lats)],
    [Math.max(...lngs), Math.max(...lats)],
  ];
}

function getBoundsFromMetadata(metadata) {
  if (Array.isArray(metadata?.bounds) && metadata.bounds.length === 4) {
    return [[metadata.bounds[0], metadata.bounds[1]], [metadata.bounds[2], metadata.bounds[3]]];
  }
  const extent = metadata?.fullExtent || metadata?.extent;
  if (!extent)
    throw new Error('ArcGIS metadata does not include fullExtent.');
  const sr = extent.spatialReference || metadata?.spatialReference;
  const corners = [
    projectPointToLngLat(extent.xmin, extent.ymin, sr),
    projectPointToLngLat(extent.xmin, extent.ymax, sr),
    projectPointToLngLat(extent.xmax, extent.ymin, sr),
    projectPointToLngLat(extent.xmax, extent.ymax, sr),
  ];
  return normalizeLngLatBounds(corners);
}

/**
 * Fetches the ArcGIS service metadata JSON.
 * Returns null on failure (non-throwing) so callers can degrade gracefully.
 * The raw metadata is returned so it can be reused for both format selection
 * (MapServer) and the flyTo bounds calculation, avoiding a second round-trip.
 *
 * @param {object} serviceInfo  Result of {@link normalizeArcgisServiceUrl}.
 * @param {string} token
 * @returns {Promise<object|null>}
 */
async function fetchServiceMetadata(serviceInfo, token) {
  try {
    const url = buildMetadataUrl(serviceInfo, token);
    const response = await fetch(url);
    if (!response.ok)
      throw new Error(`Metadata fetch failed: ${response.status}`);
    const metadata = await response.json();
    if (metadata?.error)
      throw new Error(metadata.error.message || 'ArcGIS metadata error');
    return metadata;
  }
  catch (err) {
    console.warn('[useArcgisOverlay] Could not fetch service metadata:', err.message);
    return null;
  }
}

// ---------------------------------------------------------------------------
// Module-level singleton state
// Shared across all callers so footer and any other consumer see the same ref.
// ---------------------------------------------------------------------------
const overlay_layer_active = ref(false);

/**
 * True while the ArcGIS token is being fetched from the API.
 * Consumers (e.g. the footer button) should disable/show a loader during this time.
 */
const overlay_token_loading = ref(false);

// Cached token response: { token: string, expiresAt: number }
let _cachedTokenResponse = null;

// Pending fetch promise to prevent concurrent fetches
let _tokenPromise = null;

// Keeps a reference to the style.load handler so it can be removed later.
let _styleLoadHandler = null;

// ---------------------------------------------------------------------------
// Composable
// ---------------------------------------------------------------------------

/**
 * useArcgisOverlay
 *
 * Manages a single ArcGIS ImageServer raster overlay on top of a Mapbox GL map.
 *
 * Features:
 * - Token fetched dynamically: On mount, fetches a token from the internal API
 *   and exposes `overlay_token_loading` so callers can show a loader.
 * - Self-managing: registers a persistent `style.load` listener so the overlay
 *   survives Mapbox base-style switches automatically.
 * - Fly-to: fetches the service metadata and calls `map.fitBounds()` the first
 *   time the overlay is enabled. Pass `flyOnEnable: false` to disable.
 *
 * Usage:
 *   const { overlay_layer_active, overlay_token_loading, toggleOverlayLayer } = useArcgisOverlay({ url, opacity });
 *   toggleOverlayLayer(mapboxMapInstance);
 *
 * @param {object} [options]
 * @param {string}  [options.url]                     ArcGIS service URL — ImageServer or MapServer (with optional
 *                                                    layer-index suffix, e.g. …/MapServer/0).
 *                                                    The correct export path and query params are appended automatically.
 * @param {number}  [options.opacity=0.85]            Raster opacity (0–1).
 * @param {boolean} [options.flyOnEnable=true]        Fly to layer bounds when enabled.
 */
export function useArcgisOverlay({
  url = null,
  opacity = null,
  flyOnEnable = true,
} = {}) {
  const terra_store = useTerraStore();

  const activeUrl = computed(() => {
    if (url !== null && url !== '') return url;
    const firstLayer = terra_store.container?.properties?.arcgis?.layers?.[0];
    return firstLayer?.url || '';
  });

  const activeOpacity = computed(() => {
    if (opacity !== null) return opacity;
    return terra_store.container?.properties?.arcgis?.layers?.[0]?.opacity ?? 0.85;
  });

  // -------------------------------------------------------------------------
  // Token initialisation
  // -------------------------------------------------------------------------

  /**
   * Fetches the ArcGIS token once and caches it.
   * Sets overlay_token_loading while the request is in-flight.
   * Only fetches a new token if the cached one is expired or close to expiration.
   *
   * @returns {Promise<string|null>}
   */
  async function initToken() {
    const now = Date.now();
    // Use cached token if it exists and hasn't expired (with a 10s buffer)
    if (_cachedTokenResponse && _cachedTokenResponse.expiresAt > now + 10000) {
      return _cachedTokenResponse.token;
    }

    if (_tokenPromise) return _tokenPromise;

    overlay_token_loading.value = true;
    _tokenPromise = services.common.get_arcgis_token().then((response) => {
      if (response?.data?.token) {
        _cachedTokenResponse = {
          token: response?.data?.token,
          expiresAt: response?.data?.expiresAt || (Date.now() + 3600 * 1000), // Default 1 hour fallback
        };
      }
      overlay_token_loading.value = false;
      _tokenPromise = null;
      return _cachedTokenResponse.token;
    }).catch((err) => {
      console.error('[useArcgisOverlay] Could not fetch ArcGIS token:', err.message);
      overlay_token_loading.value = false;
      _tokenPromise = null;
      return null;
    });

    return _tokenPromise;
  }

  // -------------------------------------------------------------------------
  // Private helpers
  // -------------------------------------------------------------------------

  /**
   * Adds/shows the raster tile layer.
   * `metadata` is passed in from the caller so format selection (MapServer)
   * does not require an additional network request.
   *
   * @param {mapboxgl.Map} map
   * @param {string}       token
   * @param {object|null}  [metadata]  Pre-fetched service metadata.
   */
  function _applyLayer(map, token, metadata = null) {
    if (!activeUrl.value) return; // no URL configured — nothing to render
    const serviceInfo = normalizeArcgisServiceUrl(activeUrl.value);
    const resolvedTileUrl = buildTileUrl(serviceInfo, token, metadata);
    try {
      if (!map.getSource(ARCGIS_SOURCE_ID)) {
        map.addSource(ARCGIS_SOURCE_ID, {
          type: 'raster',
          tiles: [resolvedTileUrl],
          tileSize: 256,
        });
      }
      if (!map.getLayer(ARCGIS_LAYER_ID)) {
        map.addLayer({
          id: ARCGIS_LAYER_ID,
          type: 'raster',
          source: ARCGIS_SOURCE_ID,
          paint: { 'raster-opacity': activeOpacity.value },
        });
      }
      map.setLayoutProperty(ARCGIS_LAYER_ID, 'visibility', 'visible');
    }
    catch (err) {
      console.error('[useArcgisOverlay] Failed to add overlay layer:', err);
    }
  }

  /**
   * Fits the map to the layer extent derived from already-fetched metadata.
   * No-op when metadata is null or the extent cannot be parsed.
   *
   * @param {mapboxgl.Map} map
   * @param {object|null}  metadata
   */
  function _flyToLayerFromMetadata(map, metadata) {
    if (!metadata) return;
    try {
      const bounds = getBoundsFromMetadata(metadata);
      map.fitBounds(bounds, { padding: 60, maxZoom: 16, duration: 1200 });
    }
    catch (err) {
      console.warn('[useArcgisOverlay] Could not compute layer bounds for flyTo:', err.message);
    }
  }

  // -------------------------------------------------------------------------
  // Public API
  // -------------------------------------------------------------------------

  /**
   * Enables the ArcGIS overlay.
   * - Awaits the token before applying the layer.
   * - Registers a persistent `style.load` listener (survives base-style changes).
   * - Optionally flies to the layer extent on first enable.
   */
  async function addArcgisOverlayLayer(map) {
    if (!map) return;

    const token = await initToken();

    // Fetch metadata once — reused for MapServer format selection and flyTo bounds.
    const serviceInfo = normalizeArcgisServiceUrl(activeUrl.value);
    const metadata = await fetchServiceMetadata(serviceInfo, token);

    // Register a persistent handler so the overlay survives style switches.
    if (!_styleLoadHandler) {
      _styleLoadHandler = () => _applyLayer(map, token, metadata);
      map.on('style.load', _styleLoadHandler);
    }

    // Apply immediately if style is already ready, otherwise wait once.
    if (map.isStyleLoaded()) {
      _applyLayer(map, token, metadata);
    }
    else {
      map.once('style.load', () => _applyLayer(map, token, metadata));
    }

    // Fly to layer bounds using the already-fetched metadata (no extra request).
    if (flyOnEnable)
      _flyToLayerFromMetadata(map, metadata);
  }

  /**
   * Disables the ArcGIS overlay and removes the `style.load` listener.
   */
  function removeArcgisOverlayLayer(map) {
    if (!map) return;

    if (_styleLoadHandler) {
      map.off('style.load', _styleLoadHandler);
      _styleLoadHandler = null;
    }

    try {
      if (map.getLayer(ARCGIS_LAYER_ID))
        map.removeLayer(ARCGIS_LAYER_ID);
      if (map.getSource(ARCGIS_SOURCE_ID))
        map.removeSource(ARCGIS_SOURCE_ID);
    }
    catch (err) {
      console.error('[useArcgisOverlay] Failed to remove overlay layer:', err);
    }
  }

  /**
   * Toggles the overlay on/off.
   * @param {mapboxgl.Map} map
   */
  function toggleOverlayLayer(map) {
    overlay_layer_active.value = !overlay_layer_active.value;
    if (overlay_layer_active.value)
      addArcgisOverlayLayer(map);
    else
      removeArcgisOverlayLayer(map);
  }

  return {
    /** Reactive boolean — true when the overlay is currently shown. */
    overlay_layer_active,
    /** Reactive boolean — true while the ArcGIS token is being fetched. */
    overlay_token_loading,
    toggleOverlayLayer,
  };
}
